/* eslint-disable complexity */ /* Will be refactored once we have redesign goes live */
import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { ContentText } from "@/components/adg4/price-card/src/content-text";
import {
    CloudOfferingsWithSkus,
    DefaultPlanLimits,
    FormattedPriceCalculationResponse,
    SpecialPlanNames,
    SpecialProductKeys
} from "@/components/wpl/pricing/src/price-finder/constants.tsx";
import { DEFAULT_VALUES } from "@/libraries/web-framework/src/localization/values";

import { usePriceFinderContext } from "../price-finder-context.tsx";
import { usePriceFinderStore } from "../store/store.tsx";

import { BilledAnnuallyButton } from "./billed-annually-button.tsx";
import { PriceCardProps } from "./price-card.tsx";
import { UserTier } from "./user-tier.tsx";

const fontCharlieDisplay =
    '"Charlie Display", "Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI",' +
    'Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif !important';

const priceSpanStyles = css({
    fontSize: "48px",
    fontWeight: 500,
    fontFamily: fontCharlieDisplay,
    position: "relative"
});

export const Price = (props: PriceCardProps) => {
    const {
        billingCycle,
        overSignupLimit,
        planName,
        currencyISOCode,
        unitCount,
        productKey,
        priceCalcIdx,
        contactURL,
        offeringSku,
        shouldAddDiscount
    } = props;
    const [{ priceCalcs, messages }, actions] = usePriceFinderStore();

    const { enterpriseMinimumCount = DefaultPlanLimits.UNIT_MIN_ENTERPRISE } =
        usePriceFinderContext();

    const priceCalc = priceCalcs[priceCalcIdx];
    if (!priceCalc) {
        throw new Error(
            `Price calculation was not found for index ${priceCalcIdx}`
        );
    }
    let planMessage: ReactNode =
        billingCycle === "MONTHLY" ? (
            <MonthlyPrice
                formattedPrice={priceCalc.formattedAvgPerUnitPrice}
                formattedAvgPerUnitPriceWithDiscount={
                    priceCalc.formattedAvgPerUnitPriceWithDiscount
                }
                planName={planName}
                shouldAddDiscount={shouldAddDiscount}
            />
        ) : (
            <AnnualPrice
                formattedPrice={priceCalc.formattedTotalPrice}
                formattedTotalPriceWithDiscount={
                    priceCalc.formattedTotalPriceWithDiscount
                }
                planName={planName}
                priceCalc={priceCalc}
                priceDisplaySize={
                    currencyISOCode === "JPY" ? "small" : "normal"
                }
                shouldAddDiscount={shouldAddDiscount}
            />
        );

    if (overSignupLimit && productKey === SpecialProductKeys.BITBUCKET) {
        planMessage = (
            <ContentText variant="md" data-testid="bitbucket-signup-limit">
                For teams of more than 35,000,{" "}
                <a href={contactURL}>contact our sales team.</a>
            </ContentText>
        );
    } else if (overSignupLimit) {
        planMessage = (
            <ContentText variant="md" data-testid="signup-limit">
                {(messages?.cardOverSignupLimit?.defaultMessage as string) ??
                    ""}
            </ContentText>
        );
    }

    if (offeringSku === CloudOfferingsWithSkus.rovo && unitCount > 1000) {
        planMessage = (
            <ContentText variant="md" data-testid="rovo-annual-message">
                <FormattedMessage
                    id="Pricing.card.body.rovo.annual.message"
                    defaultMessage="Start your free 30-day trial or contact your Atlassian sales representative for more information."
                />
            </ContentText>
        );
    }

    switch (planName) {
        case SpecialPlanNames.STARTER:
        case SpecialPlanNames.FREE:
            planMessage = (
                <FreePrice
                    formattedPrice={priceCalc.formattedTotalPrice}
                    planName={planName}
                />
            );
            break;
        case SpecialPlanNames.ENTERPRISE:
            if (messages.cardEnterpriseBodyOverride) {
                planMessage = messages.cardEnterpriseBodyOverride;
            } else if (billingCycle === "MONTHLY") {
                planMessage = (
                    <ContentText variant="md" data-testid="enterprise-monthly">
                        <BilledAnnuallyButton
                            onClick={() => actions.updateBillingCycle("ANNUAL")}
                        />
                        <FormattedMessage
                            id="PricingTeam24.card.body.enterprise.monthly"
                            defaultMessage="Switch to Annual billing above to view Enterprise pricing."
                        />
                    </ContentText>
                );
            } else if (currencyISOCode === "JPY") {
                planMessage = (
                    <ContentText variant="md" data-testid="enterprise-jpy">
                        <FormattedMessage
                            id="Pricing.card.body.enterprise.jpy"
                            defaultMessage="For pricing details see FAQ below or talk to our sales team"
                        />
                    </ContentText>
                );
            } else if (overSignupLimit) {
                planMessage = (
                    <ContentText
                        variant="md"
                        data-testid="enterprise-signup-limit"
                    >
                        <FormattedMessage
                            id="Pricing.card.body.enterprise.overSignupLimit"
                            defaultMessage="Talk to our sales team about pricing for Cloud Enterprise"
                        />
                    </ContentText>
                );
            } else if (unitCount < enterpriseMinimumCount) {
                planMessage = (
                    <ContentText
                        variant="md"
                        data-testid="enterprise-min-limit"
                    >
                        {messages.cardEnterpriseMinimumCount && (
                            <FormattedMessage
                                id={messages.cardEnterpriseMinimumCount.id}
                                defaultMessage={
                                    messages.cardEnterpriseMinimumCount
                                        .defaultMessage
                                }
                                values={{
                                    ...DEFAULT_VALUES,
                                    enterpriseMinimumCount
                                }}
                            />
                        )}
                    </ContentText>
                );
            }
            break;
    }

    return planMessage;
};

interface PriceDisplayProps {
    formattedPrice: string;
    planName: string;
    shouldAddDiscount?: boolean;
    formattedAvgPerUnitPriceWithDiscount?: string;
    formattedTotalPriceWithDiscount?: string;
}

const FREE_PRICE_TEST_ID = "free-price";

const FreePrice = (props: PriceDisplayProps) => {
    return (
        <ContentText
            variant="xl"
            data-cy={`plan-card-price-${props.planName.toLowerCase()}`}
            data-testid={FREE_PRICE_TEST_ID}
        >
            {props.formattedPrice}
        </ContentText>
    );
};

const MONTHLY_PRICE_TEST_ID = "monthly-price";

const MonthlyPrice = (props: PriceDisplayProps) => {
    const [{ messages }] = usePriceFinderStore();
    return (
        <>
            {props.shouldAddDiscount ? (
                <ContentText
                    variant="xl"
                    data-cy={`plan-card-price-${props.planName.toLowerCase()}`}
                    data-testid={MONTHLY_PRICE_TEST_ID}
                >
                    {props.formattedAvgPerUnitPriceWithDiscount}
                </ContentText>
            ) : (
                <ContentText
                    variant="xl"
                    data-cy={`plan-card-price-${props.planName.toLowerCase()}`}
                    data-testid={MONTHLY_PRICE_TEST_ID}
                >
                    {props.formattedPrice}
                </ContentText>
            )}
            <ContentText variant="md">
                {messages?.cardMonthlyPerUnitCount && (
                    <FormattedMessage
                        id={messages.cardMonthlyPerUnitCount.id}
                        defaultMessage={
                            messages.cardMonthlyPerUnitCount.defaultMessage
                        }
                    />
                )}
            </ContentText>
        </>
    );
};

interface AnnualPriceProps extends PriceDisplayProps {
    priceCalc: FormattedPriceCalculationResponse;
    priceDisplaySize: "normal" | "small";
    shouldAddDiscount?: boolean;
    formattedUnitTierNode?: ReactNode;
}

const ANNUAL_PRICE_TEST_ID = "annual-price";

export const AnnualPriceV3 = (props: AnnualPriceProps) => {
    return (
        <>
            {props.shouldAddDiscount ? (
                <ContentText
                    variant="xl"
                    style={{
                        fontSize:
                            props.priceDisplaySize === "small"
                                ? "35px"
                                : undefined
                    }}
                    data-testid={ANNUAL_PRICE_TEST_ID}
                    data-cy={`plan-card-price-${props.planName.toLowerCase()}`}
                >
                    {props.formattedTotalPriceWithDiscount}
                </ContentText>
            ) : (
                <span css={priceSpanStyles}>{props.formattedPrice}</span>
            )}
            <div
                data-testid={ANNUAL_PRICE_TEST_ID}
                css={css({ marginBlockStart: "-16px" })}
            >
                <FormattedMessage
                    id="Pricing.PriceCard.Annual.Description"
                    defaultMessage="per year"
                />
            </div>
            {props.formattedUnitTierNode}
        </>
    );
};

export const AnnualPrice = (props: AnnualPriceProps) => {
    const [{ unitCount }] = usePriceFinderStore();
    const { annualUserTierDisplayThreshold } = usePriceFinderContext();

    const shouldDisplayPriceTier =
        annualUserTierDisplayThreshold === undefined ||
        unitCount >= annualUserTierDisplayThreshold;

    return (
        <>
            {props.shouldAddDiscount ? (
                <ContentText
                    data-testid={ANNUAL_PRICE_TEST_ID}
                    variant="xl"
                    style={{
                        fontSize:
                            props.priceDisplaySize === "small"
                                ? "35px"
                                : undefined
                    }}
                    data-cy={`plan-card-price-${props.planName.toLowerCase()}`}
                >
                    {props.formattedTotalPriceWithDiscount}
                </ContentText>
            ) : (
                <ContentText
                    data-testid={ANNUAL_PRICE_TEST_ID}
                    variant="xl"
                    style={{
                        fontSize:
                            props.priceDisplaySize === "small"
                                ? "35px"
                                : undefined
                    }}
                    data-cy={`plan-card-price-${props.planName.toLowerCase()}`}
                >
                    {props.formattedPrice}
                </ContentText>
            )}

            <ContentText data-testid={ANNUAL_PRICE_TEST_ID} variant="md">
                <FormattedMessage
                    id="Pricing.PriceCard.Annual.Description"
                    defaultMessage="per year"
                />
                {shouldDisplayPriceTier && (
                    <UserTier priceCalc={props.priceCalc} />
                )}
            </ContentText>
        </>
    );
};
